/**
 * Название стенда, когда на визитке его нет.
 *
 * Такие карточки в этом деле обычны: два имени, «CO FOUNDER», телефоны и
 * почта — и ни одной строки с названием компании. Модель правильно не
 * выдумывает то, чего не напечатано, но человек у стенда упирался в
 * «не хватает названия компании» и не мог пройти дальше.
 *
 * Между тем торговое имя на карточке есть — в почте и в соцсетях:
 * sobail.trading@gmail.com, @sobail.trading. Его и предлагаем — одним
 * нажатием, с возможностью поправить. Не подставляем молча: догадка должна
 * остаться видимой человеку.
 */

/** Почтовые службы, чей домен ничего не говорит о компании. */
const FREE_MAIL = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.jp', 'hotmail.com',
  'outlook.com', 'live.com', 'icloud.com', 'me.com', 'aol.com', 'gmx.com',
  'qq.com', '163.com', '126.com', 'sina.com', 'foxmail.com', 'naver.com',
  'mail.ru', 'yandex.ru', 'proton.me', 'protonmail.com', 'walla.co.il',
]);

/** Ящики вида info@, sales@ — про компанию говорит домен, а не эта часть. */
const GENERIC = new Set([
  'info', 'sales', 'contact', 'office', 'mail', 'admin', 'export', 'import',
  'support', 'hello', 'enquiry', 'inquiry', 'marketing', 'trade', 'shop',
]);

/** «sobail.trading» → «Sobail Trading». Точки и подчёркивания — это пробелы. */
function pretty(raw: string): string {
  return raw
    .replace(/[._\-+]+/g, ' ')
    .replace(/\d{2,}/g, ' ')
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 1)
    .map((w) => (w.length <= 3 ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ')
    .trim();
}

function fromEmail(email: string): string {
  const [local, domain] = email.toLowerCase().split('@');
  if (!domain) return '';
  const host = domain.replace(/^www\./, '');
  if (!FREE_MAIL.has(host)) {
    // Свой домен — самое надёжное: tnkdiam.com → «Tnkdiam».
    return pretty(host.split('.')[0]);
  }
  return GENERIC.has(local) ? '' : pretty(local);
}

function fromSite(site: string): string {
  const host = site.toLowerCase()
    .replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  return host ? pretty(host.split('.')[0]) : '';
}

export interface NameHints {
  emails?: string[];
  websites?: string[];
  handles?: string[];   // wechat, line, telegram, instagram
  person?: string;
}

/** Что предложить в качестве названия. Пусто — предлагать нечего. */
export function guessCompanyName(h: NameHints): string {
  for (const site of h.websites ?? []) {
    const g = fromSite(site);
    if (g) return g;
  }
  for (const email of h.emails ?? []) {
    const g = fromEmail(email);
    if (g) return g;
  }
  for (const handle of h.handles ?? []) {
    // Ник вида @sobail.trading — то же торговое имя.
    const g = pretty(String(handle).replace(/^@/, ''));
    if (g && g.split(' ').length <= 4) return g;
  }
  // Последнее средство: карточка человека без всяких следов компании.
  const person = (h.person ?? '').trim();
  return person ? `Стенд ${person.split(/\s+/)[0]}` : '';
}
